import gsap from 'gsap'
import { useEffect, useState } from 'react'
import { useGSAP } from '@gsap/react'
import { useView } from '@10000/react'
import { Box, CenteredBox, Expertise } from '../../widgets'
import { Legend, TextScreen } from '.'
import { ComparisonCards, usePracticeWheel } from '../common'
import { practices } from '../../../lookups'

export const Results = ({ displayMode, onAnimationComplete }) => {
  const { showResult, showWheel, hide } = usePracticeWheel()
  const { presenter, question, result, teamAverages, consensusAnswers, stage } = useView('squiz')
  const practice = Math.floor(question / 2)
  const teamAverage = teamAverages[question]
  const consensus = consensusAnswers?.[0]?.answer
  const [animation, setAnimation] = useState()

  useGSAP(() => {
    if(presenter && !displayMode && typeof consensus === 'number') {
      setAnimation(gsap.timeline({ paused: true })
        .add('teamAverage')
        .call(() => showResult([teamAverage, -1]))
        .to('#team-average-card', { top: 0, duration: 2 }, '<')
        .to('#next-button', { opacity: 1, onComplete: onAnimationComplete })

        .add('consensus')
        .to('body', {})
        .call(() => showResult([teamAverage, consensus]))
        .to('#consensus-card', { top: 0, duration: 2, onComplete: onAnimationComplete }, '<')

        .add('practice')
        .to('body', {})
        .to('#compare-heading h2', { transform: 'translateY(100px)', opacity: 0, duration: 0.5 })
        .to('#result-compare-cards', { top: '-100%', duration: 1 }, '<')
        .to('#next-button', { opacity: 0 }, '<')
        .call(showWheel)
        .to('#next-button', { opacity: 1, delay: 1, onComplete: onAnimationComplete }, '<')
      )
    } else if(typeof consensus === 'number') {
      setAnimation(gsap.timeline({ paused: true })
        .add('teamAverage')
        .to('#heading1 h2', { transform: 'translateY(0)', opacity: 1, delay: 0.5, duration: 0.5 })
        .to('#heading2', { top: 0, delay: 2, duration: 0.25 })
        .to('#heading1', { top: '-100%', duration: 0 })
        .to('#heading2 h2', { transform: 'translateY(0)', opacity: 1, duration: 0.5 })
        .to('#heading2', { top: '-100%', delay: 2, duration: 0.25 })
        .call(() => showResult([teamAverage, -1]))
        .to('#team-average-card', { top: 0, duration: 2 }, '<')
        .to('#legend', { opacity: 1, delay: 1 }, '<')
        .to('#next-button', { opacity: 1 })

        .add('consensus')
        .to('body', {})
        .call(() => showResult([teamAverage, consensus]))
        .to('#consensus-card', { top: 0, duration: 2 }, '<')

        .add('practice')
        .to('body', {})
        .to('#compare-heading h2', { transform: 'translateY(100px)', opacity: 0, duration: 0.5 })
        .to('#result-compare-cards', { top: '-100%', duration: 1 }, '<')
        .to('#next-button', { opacity: 0 }, '<')
        .call(showWheel)
        .to('#next-button', { opacity: 1, delay: 1 }, '<')
      )
    } else {
      setAnimation(gsap.timeline({ paused: true })
        .add('teamAverage')
        .to('#result-expertise', { opacity: 1, duration: 0.5 })
        .call(() => showResult(teamAverage), [], '<')
        .to('#next-button', { opacity: 1 }, '<')
        .to('#legend', { opacity: 1, delay: 1, onComplete: onAnimationComplete }, '<')

        .add('practice')
        .to('body', {})
        .to('#result-expertise', { opacity: 0, duration: 0.5 })
        .to('#next-button', { opacity: 0 }, '<')
        .call(showWheel)
        .to('#next-button', { opacity: 1, delay: 1, onComplete: onAnimationComplete }, '<')
      )
    }
  })

  useGSAP(() => {
    if(animation && result) {
      animation.seek(result)
      animation.tweenTo(animation.nextLabel())
    }
  }, [animation, result])

  useEffect(() => {
    if((presenter && !displayMode) || stage === 'analytics' || stage === 'exited' || stage === 'question-option'){
      return () => hide()
    }
  }, [hide, stage, presenter, displayMode])

  return (
    <Box
      container
      fullWidth
      fullHeight
      justifyContent="center"
    >
      <Box
        column
        fullWidth
        fullHeight
        maxWidth={1280}
        p="120px 20px 0 20px"
        justifyContent="space-between"
        alignItems="center"
        alignSelf="center"
        zIndex={1}
      >
        <Expertise
          id="result-expertise"
          gap="5dvh"
          opacity={0}
          value={teamAverage}
          showValue={false}
          expertiseSize={36}
          title={practices[practice]?.name.toUpperCase()}
          animate
        />
      </Box>

      <CenteredBox
        column
        gap={16}
        position="absolute"
        fullWidth
        fullHeight
        top={120}
        zIndex={1}
      >
        <ComparisonCards
          container
          id="result-compare-cards"
          displayMode={displayMode}
          fullWidth
          maxWidth={1920}
          justifyContent="space-between"
        />
      </CenteredBox>

      {displayMode && <Legend id="legend" />}

      <TextScreen id="heading1" background="black" color="white" text="Consensus is over!" />
      <TextScreen id="heading2" top="-100%" background="var(--blue)" color="white" text="Let's see how we did" zIndex={1} />
      <TextScreen id="compare-heading" opacity={0} background="transparent" color="white" text="Comparison" mt={displayMode ? -700 : -450} />
    </Box>
  )
}
